import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { type Node, useReactFlow, useStoreApi } from 'reactflow';
import type { NodeData } from '../nodes/flowEditorBlockNodeTypes';
import { findConversationMatches } from '../conversationFind';
import { panViewportToNode } from './viewportPan';

// Hold off the auto-pan until typing settles; a per-keystroke jump read as
// jumpy.
const TYPE_PAN_DEBOUNCE = 250;

// Jumps requested faster than this land instantly instead of animating, so
// held-Enter cycling reads as discrete steps rather than continuous flight.
const RAPID_CYCLE_MS = 300;

export type ConversationFind = {
  isOpen: boolean;
  query: string;
  // One containing-node id per matching text run, in document order.
  matches: string[];
  currentIndex: number;
  // Null when the search is closed or has no matches.
  currentNodeId: string | null;
  matchedNodeIds: ReadonlySet<string>;
  // Ordinal of the current match among its node's matching runs; -1 when none.
  // See TiptapHighlight for how the renderer uses it.
  currentRun: number;
  open: () => void;
  close: () => void;
  setQuery: (query: string) => void;
  next: () => void;
  previous: () => void;
};

// Matches recompute from the live nodes on every keystroke; conversations are
// small enough that a full re-scan is cheap.
export function useConversationFind(nodes: Node<NodeData>[]): ConversationFind {
  const { getNode, getViewport, setViewport } = useReactFlow();
  const store = useStoreApi();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQueryState] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  // A new jump cancels any in-flight pan so two never fight over the viewport.
  const cancelPan = useRef<(() => void) | null>(null);
  const lastJumpAt = useRef(0);
  const panToNode = useCallback(
    (nodeId: string) => {
      const node = getNode(nodeId);
      if (node === undefined) {
        return;
      }
      const { width, height } = store.getState();
      const now = performance.now();
      const snap = now - lastJumpAt.current < RAPID_CYCLE_MS;
      lastJumpAt.current = now;
      cancelPan.current?.();
      cancelPan.current = panViewportToNode({
        node,
        viewport: getViewport(),
        container: { width, height },
        setViewport,
        snap,
      });
    },
    [getNode, getViewport, setViewport, store],
  );
  useEffect(() => () => cancelPan.current?.(), []);

  const matches = useMemo(
    () => (isOpen ? findConversationMatches(nodes, query) : []),
    [isOpen, nodes, query],
  );

  const clampedIndex = matches.length === 0 ? -1 : Math.min(currentIndex, matches.length - 1);
  const currentNodeId = clampedIndex === -1 ? null : (matches[clampedIndex] ?? null);

  const matchedNodeIds = useMemo(() => new Set(matches), [matches]);

  // Pairs with the renderer's run cursor (see FindRunCursor).
  const currentRun = useMemo(() => {
    if (clampedIndex === -1) {
      return -1;
    }
    let run = 0;
    for (let i = 0; i < clampedIndex; i += 1) {
      if (matches[i] === currentNodeId) {
        run += 1;
      }
    }
    return run;
  }, [matches, clampedIndex, currentNodeId]);

  const jumpTo = useCallback(
    (index: number) => {
      const nodeId = matches[index];
      if (nodeId !== undefined) {
        panToNode(nodeId);
      }
    },
    [matches, panToNode],
  );

  // Recomputes from the live nodes rather than closing over `matches`, so the
  // trailing debounced call always reflects the final keystroke.
  const panToFirstMatch = useDebouncedCallback((nextQuery: string) => {
    const first = findConversationMatches(nodes, nextQuery)[0];
    if (first !== undefined) {
      panToNode(first);
    }
  }, TYPE_PAN_DEBOUNCE);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    panToFirstMatch.cancel();
    cancelPan.current?.();
    setIsOpen(false);
    setQueryState('');
    setCurrentIndex(0);
  }, [panToFirstMatch]);

  const setQuery = useCallback(
    (next: string) => {
      setQueryState(next);
      setCurrentIndex(0);
      panToFirstMatch(next);
    },
    [panToFirstMatch],
  );

  const next = useCallback(() => {
    if (matches.length === 0) {
      return;
    }
    // A deliberate jump wins over any pan still queued from typing.
    panToFirstMatch.cancel();
    const index = (clampedIndex + 1) % matches.length;
    setCurrentIndex(index);
    jumpTo(index);
  }, [matches.length, clampedIndex, jumpTo, panToFirstMatch]);

  const previous = useCallback(() => {
    if (matches.length === 0) {
      return;
    }
    panToFirstMatch.cancel();
    const index = (clampedIndex - 1 + matches.length) % matches.length;
    setCurrentIndex(index);
    jumpTo(index);
  }, [matches.length, clampedIndex, jumpTo, panToFirstMatch]);

  return {
    isOpen,
    query,
    matches,
    currentIndex: clampedIndex,
    currentNodeId,
    matchedNodeIds,
    currentRun,
    open,
    close,
    setQuery,
    next,
    previous,
  };
}
